import { useGetTradableMarketAccountNamesForMultileg } from '@src/hooks/useGetTradableMarketAccountNamesForMultileg';
import { useAppStateDispatch, useAppStateSelector } from '@src/providers/AppStateProvider';
import { useTradingSettings } from '@src/providers/TradingSettingsContext';
import { useEffect, useRef } from 'react';
import {
  hydrateMultilegState,
  initMultilegComboState,
  selectIsOptionsReferenceDataReady,
  selectIsSecuritiesReferenceDataReady,
} from '../MultilegComboSlice';
import type { MultilegComboType } from '../enums';

/**
 * Hooks that hydrates the redux slice with the persisted tab state
 */
export function useMultilegTabState({
  panelID,
  instrumentType,
  compactOrderBook,
  initiatingLegs,
  haircutRatios,
  symbol,
  legSymbols,
  legMarketAccounts,
  legMarkets,
}: {
  panelID: string;
  instrumentType: MultilegComboType;
  compactOrderBook?: boolean;
  initiatingLegs?: boolean[];
  haircutRatios?: string[];
  symbol?: string;
  legSymbols?: string[];
  legMarketAccounts?: string[];
  legMarkets?: string[];
}) {
  const dispatch = useAppStateDispatch();
  const { newMultilegDefaultDelta1Behaviour } = useTradingSettings();
  const getTradableMarketAccountNames = useGetTradableMarketAccountNamesForMultileg();
  const newMultilegDefaultDelta1BehaviourRef = useRef(newMultilegDefaultDelta1Behaviour);
  const getTradableMarketAccountNamesRef = useRef(getTradableMarketAccountNames);
  newMultilegDefaultDelta1BehaviourRef.current = newMultilegDefaultDelta1Behaviour;
  const isOptionsDataReady = useAppStateSelector(selectIsOptionsReferenceDataReady);
  const isSecuritiesDataReady = useAppStateSelector(selectIsSecuritiesReferenceDataReady);

  useEffect(() => {
    dispatch(initMultilegComboState(panelID));
  }, [panelID, dispatch]);

  useEffect(() => {
    if (isOptionsDataReady || isSecuritiesDataReady) {
      dispatch(
        hydrateMultilegState({
          instrumentType,
          compactOrderBook,
          initiatingLegs,
          haircutRatios,
          resolvedSymbol: symbol,
          legSymbols,
          legMarketAccounts,
          legMarkets,
          newMultilegDefaultingContext: {
            behaviour: newMultilegDefaultDelta1BehaviourRef.current,
            getTradableMarketAccountNames: getTradableMarketAccountNamesRef.current,
          },
          panelID,
        })
      );
    }
  }, [
    dispatch,
    instrumentType,
    compactOrderBook,
    initiatingLegs,
    haircutRatios,
    symbol,
    legSymbols,
    legMarketAccounts,
    legMarkets,
    panelID,
    isOptionsDataReady,
    isSecuritiesDataReady,
  ]);
}
